// src/modules/reclamos/pages/ReclamoDetalle.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, Divider, Grid, Paper, Stack, TextField, Typography,
  Snackbar, Alert, IconButton, Tooltip, Menu, MenuItem, useMediaQuery, useTheme
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useParams } from "react-router-dom";
import { reclamosApi } from "../services/reclamosApi";
import ReasignarDialog from "../components/ReasignarDialog";
import ENV from "../../../config/env";
import { useVehiculoViewer } from "../../../context/VehiculoViewerContext";

const ESTADOS = ["NUEVO", "REVISION", "EN_PROCESO", "EN_ESPERA", "MANTENIMIENTO", "RESUELTO", "CERRADO", "REABIERTO"];

const joinUrl = (base, path) => {
  if (!base || !path) return undefined;
  return base.replace(/\/+$/, "") + "/" + String(path).replace(/^\/+/, "");
};

const colorForPrioridad = (p) =>
  p === "URGENTE" ? "error" : p === "ALTA" ? "warning" : p === "MEDIA" ? "info" : "default";

function safeJSON(x) {
  if (!x) return null;
  if (typeof x === "object") return x;
  try { return JSON.parse(x); } catch { return null; }
}
function formatDate(dt) {
  try { return new Date(dt).toLocaleString("es-BO"); } catch { return ""; }
}
function parseDT(x) { try { return new Date(x).getTime(); } catch { return 0; } }
const norm = (s) => (s ?? "").toString().trim().toUpperCase();

function decodeJwtUsername(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(decodeURIComponent(escape(atob(base64))));
    return (
      json.username ||
      json.user?.username ||
      json.preferred_username ||
      json.email?.split("@")[0] ||
      json.sub ||
      null
    );
  } catch { return null; }
}
function getCurrentUsername() {
  try {
    const raw = localStorage.getItem("profile") || sessionStorage.getItem("profile");
    if (raw) {
      const p = JSON.parse(raw);
      const u = p.username || p.user?.username || p.login?.username || p.account?.username;
      if (u) return u;
    }
    const loose =
      localStorage.getItem("username") ||
      sessionStorage.getItem("username") ||
      window.__USER?.username;
    if (loose) return loose;
    const jwt =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("access_token") ||
      sessionStorage.getItem("token");
    const fromJwt = jwt ? decodeJwtUsername(jwt) : null;
    if (fromJwt) return fromJwt;
    if (ENV?.DEBUG_FORCE_USERNAME) return ENV.DEBUG_FORCE_USERNAME;
    return null;
  } catch { return null; }
}
function getCreatorUsername(reclamo) {
  return (
    reclamo?.id_usuario ||
    reclamo?.creado_por ||
    reclamo?.usuario_creador ||
    reclamo?.username_creador ||
    null
  );
}
function canCloseOrReopen(reclamo) {
  const current = (getCurrentUsername() || "").trim().toLowerCase();
  const creator = (getCreatorUsername(reclamo) || "").trim().toLowerCase();
  return !!current && !!creator && current === creator;
}

/** Burbujita de mensaje */
function MessageBubble({ m, usuariosByUsername }) {
  const autorLabel = usuariosByUsername[m.id_autor] || m.id_autor || "—";
  const picture = (() => {
    if (!m.profile_picture) return "/default-avatar.png";
    if (/^https?:\/\//i.test(m.profile_picture)) return m.profile_picture;
    return joinUrl(ENV.API_BASE_URL, m.profile_picture);
  })();

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        //bgcolor: m.rol_autor === "AGENTE" ? "grey.50" : "grey.100",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.200",
        maxWidth: { xs: "100%", sm: "80%" }, // Responsive: 100% en móvil, 80% en sm y superior
        ml: m.rol_autor === "AGENTE" ? { xs: 0, sm: "auto" } : 0, // Mensajes de agente a la derecha en sm+ (opcionalmente)
        mr: m.rol_autor === "CLIENTE" ? { xs: 0, sm: "auto" } : 0, // Mensajes de cliente a la izquierda en sm+ (opcionalmente)
      }}
    >
      <Box
        component="img"
        src={picture}
        alt={autorLabel}
        sx={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
      <Box>
        <Typography variant="caption" color="text.secondary">
          {autorLabel} • {formatDate(m.creado_en)}
        </Typography>
        {m.mensaje && <Typography sx={{ mt: .5 }}>{m.mensaje}</Typography>}
      </Box>
    </Box>
  );
}

/** Agrupa mensajes bajo cada evento por rango de tiempo */
function groupMessagesByEvent(eventos, mensajes) {
  const evs = [...(eventos || [])].sort((a, b) => parseDT(a.creado_en) - parseDT(b.creado_en));
  const msgs = [...(mensajes || [])].sort((a, b) => parseDT(a.creado_en) - parseDT(b.creado_en));
  const byId = new Map(evs.map(e => [e.id, []]));
  if (evs.length === 0) return byId;
  const bounds = evs.map(e => parseDT(e.creado_en));
  for (const m of msgs) {
    const t = parseDT(m.creado_en);
    let idx = 0;
    for (let i = 0; i < bounds.length; i++) {
      if (t >= bounds[i]) idx = i; else break;
    }
    const ev = evs[idx];
    byId.get(ev.id).push(m);
  }
  return byId;
}

function TimelineGroup({ header, subtitle, children, last = false }) {
  return (
    <Box
      sx={{
        position: "relative",
        pl: 4,
        py: 1.25,
        mt: -0.5,
        mb: last ? 0 : -0.5,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 11,
          top: -8,
          bottom: last ? 10 : -8,
          width: 2,
          bgcolor: (theme) => theme.palette.divider,
        },
      }}
    >
      <Box
        sx={{
          position: "absolute", left: 6, top: 8,
          width: 12, height: 12, borderRadius: "50%",
          bgcolor: "secondary.main",
        }}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ "& *": { verticalAlign: "middle" } }}>{header}</Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export default function ReclamoDetalle() {
  const { id } = useParams();
  const theme = useTheme();
  // Hook para detectar si es pantalla pequeña (por ejemplo, menor a 'sm')
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState(null);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [sending, setSending] = useState(false);
  const [changing, setChanging] = useState(false);
  const [estadoMenuEl, setEstadoMenuEl] = useState(null);
  const [reasignarOpen, setReasignarOpen] = useState(false);
  const [usuariosMap, setUsuariosMap] = useState({});
  const [usuariosByUsername, setUsuariosByUsername] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  const openSnack = (msg, severity = "info") => setSnack({ open: true, msg, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));
  const { openVehiculoViewer } = useVehiculoViewer();

  const load = useCallback(async () => {
    const res = await reclamosApi.getById(id);
    const normalized = res?.reclamo ? res : { reclamo: res, eventos: [], mensajes: [] };
    setData(normalized);
  }, [id]);

  const reclamo = data?.reclamo ?? data ?? {};
  const eventos = data?.eventos ?? [];
  const mensajes = data?.mensajes ?? [];
  const isLoading = !data;

  const eventosOrdenados = [...eventos].sort((a, b) => parseDT(a.creado_en) - parseDT(b.creado_en));
  const mensajesByEvent = groupMessagesByEvent(eventosOrdenados, mensajes);

  const isCreator = canCloseOrReopen(reclamo);
  const estadoActual = norm(reclamo?.estado);

  // Base: quitamos NUEVO y EN_ESPERA
  const baseEstados = ESTADOS.filter(e => e !== "NUEVO" && e !== "EN_ESPERA");
  let finalEstados = baseEstados.filter(e => !["CERRADO", "REABIERTO"].includes(norm(e)));

  if (isCreator) {
    if (estadoActual === "CERRADO") {
      finalEstados = ["REABIERTO"];
    } else if (["RESUELTO", "MANTENIMIENTO"].includes(estadoActual)) {
      // El creador puede finalizar (cerrar) si está resuelto o en mantenimiento
      finalEstados.push("CERRADO");
    }
  }

  finalEstados = Array.from(new Set(finalEstados));

  useEffect(() => { load(); }, [load]);

  // autoscroll al enviar mensaje
  const msgEndRef = useRef(null);
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.mensajes?.length]);

  useEffect(() => {
    (async () => {
      try {
        const usrs = await reclamosApi.usuariosTRS({ role: "", sucursal: "La Paz" });
        const idMap = {};
        const userMap = {};
        for (const u of usrs || []) {
          const label = `${u.nombre ?? u.username}${u.nombre ? ` (${u.username})` : ""}`;
          idMap[u.id] = label;
          userMap[u.username] = label;
        }
        setUsuariosMap(idMap);
        setUsuariosByUsername(userMap);
      } catch (e) {
        console.warn("No se pudieron cargar usuarios para el timeline", e);
      }
    })();
  }, []);

  const copyLink = async () => {
    try {
      const url = window.location.href; // link actual del reclamo
      await navigator.clipboard.writeText(url);
      openSnack("Link del reclamo copiado", "success");
    } catch (e) {
      openSnack("No se pudo copiar el link", "error");
    }
  };

  const shareWhatsApp = () => {
    const url = window.location.href;
    const titulo = reclamo?.titulo || `Reclamo #${reclamo?.codigo || id}`;
    const texto = encodeURIComponent(`${titulo}\n${url}`);
    window.open(`https://wa.me/?text=${texto}`, "_blank");
  };


  const enviarMensaje = async () => {
    const texto = nuevoMensaje.trim();
    if (!texto || sending) return;
    try {
      setSending(true);
      await reclamosApi.addMensaje(id, { rol_autor: "AGENTE", mensaje: texto });
      setNuevoMensaje("");
      await load();
      openSnack("Mensaje enviado", "success");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err.message;
      openSnack(`Error${status ? ` (${status})` : ""}: ${msg}`, "error");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const openEstadoMenu = (e) => setEstadoMenuEl(e.currentTarget);
  const closeEstadoMenu = () => setEstadoMenuEl(null);

  const cambiarEstado = async (nuevo) => {
    if (!nuevo || nuevo === reclamo?.estado) return closeEstadoMenu();
    // Solo el creador puede cerrar o reabrir
    if ((nuevo === "CERRADO" || nuevo === "REABIERTO") && !isCreator) {
      openSnack("Solo el creador puede cerrar o reabrir este reclamo.", "warning");
      return closeEstadoMenu();
    }
    try {
      setChanging(true);
      await reclamosApi.cambiarEstado(id, { estado: nuevo });
      await load();
      openSnack(`Estado actualizado a ${nuevo}`, "success");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err.message;
      openSnack(`Error${status ? ` (${status})` : ""}: ${msg}`, "error");
      console.error(err);
    } finally {
      setChanging(false);
      closeEstadoMenu();
    }
  };

  const finalizarReclamo = async () => {
    if (!isCreator) {
      openSnack("Solo el creador puede finalizar este reclamo.", "warning");
      return;
    }
    if (estadoActual === "MANTENIMIENTO") {
      openSnack("No se puede finalizar desde MANTENIMIENTO. Primero reabrir.", "info");
      return;
    }
    await cambiarEstado("CERRADO");
  };

  const resolvePersona = (val) => {
    if (val === null || val === "" || val === "null" || typeof val === "undefined") {
      return "Departamento Técnico (bandeja)";
    }
    const num = Number(val);
    if (!Number.isNaN(num) && usuariosMap[num]) return usuariosMap[num];
    if (usuariosByUsername[String(val)]) return usuariosByUsername[String(val)];
    return String(val);
  };
  const resolveActor = (actor) => {
    if (!actor) return "";
    return usuariosByUsername[actor] || String(actor);
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={isLoading ? "Cargando…" : reclamo?.titulo}
        subtitle={
          !isLoading
            ? `Código: ${reclamo?.codigo} • Origen: ${reclamo?.origen} • Actualizado: ${formatDate(reclamo?.actualizado_en)}`
            : undefined
        }
        backTo="/reclamos"
        actions={
          !isLoading && (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
              <Chip label={reclamo?.estado} size="small" color="default" />
              <Chip label={reclamo?.prioridad} size="small" color={colorForPrioridad(reclamo?.prioridad)} />

              <Tooltip title="Copiar link">
                <span>
                  <IconButton size="small" onClick={copyLink}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Compartir por WhatsApp">
                <span>
                  <IconButton size="small" onClick={shareWhatsApp}>
                    <WhatsAppIcon fontSize="small" color="success" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refrescar">
                <span>
                  <IconButton size="small" onClick={load} disabled={changing || sending}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                size="small"
                variant="outlined"
                onClick={openEstadoMenu}
                disabled={changing}
              >
                {isSmallScreen ? "Estado" : "Cambiar estado"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                onClick={() => setReasignarOpen(true)}
                disabled={changing}
              >
                Reasignar
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={finalizarReclamo}
                disabled={
                  changing ||
                  !isCreator ||
                  estadoActual === "CERRADO" ||
                  estadoActual === "MANTENIMIENTO"
                }
              >
                {isSmallScreen ? "Fin" : "Finalizar"}
              </Button>
            </Stack>
          )
        }
      />

      <Menu anchorEl={estadoMenuEl} open={Boolean(estadoMenuEl)} onClose={closeEstadoMenu}>
        {finalEstados.map((e) => (
          <MenuItem
            key={e}
            selected={e === reclamo?.estado}
            onClick={() => cambiarEstado(e)}
          >
            {e}
          </MenuItem>
        ))}
      </Menu>

      <Grid container spacing={2} sx={{ p: { xs: 1.5, md: 2 } }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            {!isLoading && reclamo?.titulo && (
              <Chip
                icon={<DirectionsCarIcon fontSize="small" />}
                label="Ver vehículo"
                size="small"
                variant="outlined"
                clickable
                onClick={() => openVehiculoViewer(reclamo.titulo)}
              />
            )}

            {/* Descripción */}
            <Typography sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              {isLoading ? "…" : (reclamo?.descripcion ?? "")}
            </Typography>

            {/* Timeline */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Línea de tiempo</Typography>

            <Box  sx={{
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : "grey.100",
              borderRadius: 2,
              p: 2,
            }}>
              {isLoading && (
                <Typography variant="body2" color="text.secondary">Cargando eventos…</Typography>
              )}
              {!isLoading && eventosOrdenados.length === 0 && (
                <Typography variant="body2" color="text.secondary">Sin eventos.</Typography>
              )}

              {!isLoading && eventosOrdenados.map((ev, idx) => {
                const last = idx === eventosOrdenados.length - 1;
                const meta = safeJSON(ev.meta || ev.detalle);
                const hasKeys = meta && ("de" in meta || "from" in meta || "a" in meta || "to" in meta);
                const de = resolvePersona(meta?.de ?? meta?.from);
                const a = resolvePersona(meta?.a ?? meta?.to);
                const actor = resolveActor(meta?.actor || meta?.id_actor);

                const header = (
                  <>
                    <Chip size="small" label={ev.tipo_evento} sx={{ mr: 1 }} />
                    <Typography variant="body2" component="span">
                      {ev.nota}
                      {hasKeys && (
                        <Typography component="span" variant="body2" sx={{ ml: .5, fontStyle: "italic" }}>
                          {de} → {a}
                        </Typography>
                      )}
                      {actor && (
                        <Typography component="span" variant="body2" sx={{ ml: .5, color: "text.secondary" }}>
                          • por {actor}
                        </Typography>
                      )}
                    </Typography>
                  </>
                );

                const msgs = mensajesByEvent.get(ev.id) || [];

                return (
                  <TimelineGroup
                    key={ev.id}
                    header={header}
                    subtitle={formatDate(ev.creado_en)}
                    last={last}
                  >
                    {msgs.map((m) => (
                      <Box key={m.id} sx={{ pl: 4 }}>
                        <MessageBubble m={m} usuariosByUsername={usuariosByUsername} />
                      </Box>
                    ))}
                  </TimelineGroup>
                );
              })}
            </Box>

            {/* Mensaje */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Agregar mensaje</Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }} // Responsive: Columna en 'xs', Fila en 'sm' y superior
              spacing={1}
              alignItems="stretch"
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Escribe un mensaje…"
                value={nuevoMensaje}
                onChange={(e) => setNuevoMensaje(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && nuevoMensaje.trim()) {
                    e.preventDefault();
                    enviarMensaje();
                  }
                }}
                multiline
                maxRows={4}
                disabled={isLoading}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={enviarMensaje}
                disabled={!nuevoMensaje.trim() || sending || isLoading}
                // Ajuste de ancho para el botón en móvil
                sx={{ minWidth: { xs: '100%', sm: 'auto' }, height: { xs: 'auto', sm: 40 } }}
              >
                {isSmallScreen ? 'Enviar mensaje' : 'Enviar'}
              </Button>
            </Stack>

            <div ref={msgEndRef} />
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <ReasignarDialog
        open={reasignarOpen}
        onClose={() => setReasignarOpen(false)}
        reclamoId={id}
        responsableActualId={reclamo?.id_responsable ?? null}
        role="sistemas,tecnico,admin"
        sucursal="La Paz"
        onDone={(nuevoResponsable) => {
          setReasignarOpen(false);
          load();
          const nombre = nuevoResponsable ? (nuevoResponsable.nombre || nuevoResponsable.username) : "Departamento Técnico";
          openSnack(`Reasignado a ${nombre}`, "success");
        }}
      />
    </DashboardLayout>
  );
}